import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AftercareFlow, AftercareRun, Customer } from '@/lib/types';
import {
  Banner,
  Card,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { useSort, SortTh } from '@/components/sortable';

export function AftercarePage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [flowOpen, setFlowOpen] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [runOpen, setRunOpen] = useState(false);
  const [run, setRun] = useState({ flowId: '', customerId: '' });
  const [runStatus, setRunStatus] = useState('');

  const flows = useQuery({ queryKey: ['flows'], queryFn: () => api.get<AftercareFlow[]>('/engagement/aftercare/flows') });
  const runs = useQuery({ queryKey: ['runs'], queryFn: () => api.get<AftercareRun[]>('/engagement/aftercare/runs') });
  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });

  const createFlow = useMutation({
    mutationFn: () =>
      api.post('/engagement/aftercare/flows', {
        name: flowName,
        steps: [
          { day: 1, label: 'D1 衛教提醒' },
          { day: 3, label: 'D3 關懷追蹤' },
          { day: 7, label: 'D7 回診提醒' },
        ],
      }),
    onSuccess: () => {
      ok('關懷流程已建立');
      setFlowOpen(false);
      setFlowName('');
      void qc.invalidateQueries({ queryKey: ['flows'] });
    },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (id: string) => api.post(`/engagement/aftercare/flows/${id}/toggle`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['flows'] }),
    onError: fail,
  });
  const enqueue = useMutation({
    mutationFn: () => api.post('/engagement/aftercare/runs', run),
    onSuccess: () => {
      ok('已加入關懷佇列');
      setRunOpen(false);
      setRun({ flowId: '', customerId: '' });
      void qc.invalidateQueries({ queryKey: ['runs'] });
    },
    onError: fail,
  });
  const markSent = useMutation({
    mutationFn: (id: string) => api.post(`/engagement/aftercare/runs/${id}/sent`),
    onSuccess: () => {
      ok('已標記寄送');
      void qc.invalidateQueries({ queryKey: ['runs'] });
    },
    onError: fail,
  });

  const flowList = flows.data ?? [];
  const runList = runs.data ?? [];
  const shownRuns = runStatus ? runList.filter((r) => r.status === runStatus) : runList;
  const nameOf = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? id;
  const flowName_ = (id: string) => flowList.find((f) => f.id === id)?.name ?? id;

  const sortFlows = useSort(flowList, {
    name: (f) => f.name,
    status: (f) => (f.on ? 0 : 1),
  });
  const sortRuns = useSort(shownRuns, {
    customer: (r) => nameOf(r.customerId),
    flow: (r) => flowName_(r.flowId),
    status: (r) => (r.status === 'sent' ? 0 : 1),
  });

  return (
    <div>
      <PageHeader title="術後關懷排程" subtitle="自動化關懷流程 · 衛教與回診提醒佇列" />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="關懷流程" value={String(flowList.length)} tone="primary" />
        <Stat label="啟用中" value={String(flowList.filter((f) => f.on).length)} tone="success" />
        <Stat label="待寄送任務" value={String(runList.filter((r) => r.status === 'queued').length)} tone="warn" />
        <Stat label="已寄送" value={String(runList.filter((r) => r.status === 'sent').length)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card
          title="關懷流程"
          icon={<Icon d={I.refresh} size={14} style={{ color: 'var(--primary)' }} />}
          right={
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setFlowOpen(true)}>
              <Icon d={I.plus} size={12} /> 新流程
            </button>
          }
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <SortTh label="流程" sortKey="name" sort={sortFlows} />
                <SortTh label="狀態" sortKey="status" sort={sortFlows} />
                <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {sortFlows.sorted.map((f) => (
                <tr key={f.id}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{f.name}</td>
                  <td style={cellStyle}>
                    {f.on ? <Tag tone="success" dot>啟用</Tag> : <Tag tone="default">停用</Tag>}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => toggle.mutate(f.id)}>
                      {f.on ? '停用' : '啟用'}
                    </button>
                  </td>
                </tr>
              ))}
              {flowList.length === 0 && (
                <tr>
                  <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={3}>
                    尚無流程
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        <Card
          title="關懷任務佇列"
          icon={<Icon d={I.bell} size={14} style={{ color: 'var(--warn)' }} />}
          right={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ width: 130 }}>
                <Select value={runStatus} onChange={setRunStatus} placeholder="全部狀態" options={[{ value: '', label: '全部狀態' }, { value: 'queued', label: '待寄送' }, { value: 'sent', label: '已寄送' }]} />
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setRunOpen(true)} disabled={flowList.length === 0}>
                <Icon d={I.plus} size={12} /> 加入任務
              </button>
            </div>
          }
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <SortTh label="顧客" sortKey="customer" sort={sortRuns} />
                <SortTh label="流程" sortKey="flow" sort={sortRuns} />
                <SortTh label="狀態" sortKey="status" sort={sortRuns} />
                <th style={{ ...thStyle, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortRuns.sorted.map((r) => (
                <tr key={r.id}>
                  <td style={cellStyle}>{nameOf(r.customerId)}</td>
                  <td style={{ ...cellStyle, fontSize: 11, color: 'var(--ink-3)' }}>{flowName_(r.flowId)}</td>
                  <td style={cellStyle}>
                    {r.status === 'sent' ? <Tag tone="success" dot>已寄送</Tag> : <Tag tone="primary">待寄送</Tag>}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11 }} disabled={r.status === 'sent'} onClick={() => markSent.mutate(r.id)}>
                      標記寄送
                    </button>
                  </td>
                </tr>
              ))}
              {runList.length === 0 && (
                <tr>
                  <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>
                    尚無任務
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <Modal open={flowOpen} onClose={() => setFlowOpen(false)} width={420}>
        <ModalHeader title="新增關懷流程" sub="預設 D1 / D3 / D7 三階段" onClose={() => setFlowOpen(false)} />
        <div style={{ padding: 20 }}>
          <Field label="流程名稱 *">
            <input style={inputStyle} value={flowName} onChange={(e) => setFlowName(e.target.value)} placeholder="皮秒雷射術後關懷" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setFlowOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!flowName || createFlow.isPending} onClick={() => createFlow.mutate()}>
            建立流程
          </button>
        </div>
      </Modal>

      <Modal open={runOpen} onClose={() => setRunOpen(false)} width={440}>
        <ModalHeader title="加入關懷任務" sub="指定流程與顧客排入佇列" onClose={() => setRunOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="關懷流程 *">
            <Select
              value={run.flowId}
              onChange={(val) => setRun((s) => ({ ...s, flowId: val }))}
              placeholder="— 選擇流程 —"
              options={flowList.map((f) => ({ value: f.id, label: `${f.name}` }))}
            />
          </Field>
          <Field label="顧客 *">
            <CustomerPicker value={run.customerId} onChange={(id) => setRun((s) => ({ ...s, customerId: id }))} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setRunOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!run.flowId || !run.customerId || enqueue.isPending} onClick={() => enqueue.mutate()}>
            加入佇列
          </button>
        </div>
      </Modal>
    </div>
  );
}
